import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTERS_PATH } from '../../../routers/routers';
import { Message, PracticeStep } from '../useExercisePractice';
import { useQuestionsStore } from '../../../utils/questionStore';
import { hebrewLetters } from '../../../configs/config';
import { showInfoToast } from '../../../components/Toaster';
import { ChatService } from '../../../services/chat/chat.services';
import { getCurrentStepOptions, getNotebookPromptOptions } from './exercisePracticeResume';

const chatService = new ChatService();

export interface AgentChatParams {
  isFromLessonList?: boolean;
  topic_name: string;
  lessonId: number | null;
  levelName: string;
  lessonData: any;
  phaseIndex: number;
  fromDisplayExercise: boolean;
  currentStep: PracticeStep;
  setCurrentStep: React.Dispatch<React.SetStateAction<PracticeStep>>;
  setIsTyping: React.Dispatch<React.SetStateAction<boolean>>;
  messages: Message[];
  messagesRef: React.MutableRefObject<Message[]>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  addBotMessage: (msg: Partial<Message>, delay?: number) => Promise<string>;
  waitForMessageCompletion: (id: string) => Promise<void>;
  studentName: string;
  user_attributes: any;
  t: (key: string, options?: any) => string;

  // State variables for getCurrentStepOptions & resume logic
  selectedQuestionId: number | null;
  selectedQuestionIdRef: React.MutableRefObject<number | null>;
  setSelectedQuestionId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedQuestion: any | null;
  setSelectedQuestion: React.Dispatch<React.SetStateAction<any | null>>;
  selectedQuestionRef: React.MutableRefObject<any | null>;
  selectedQuestionLetter: string | null;
  selectedQuestionLetterRef: React.MutableRefObject<string | null>;
  setSelectedQuestionLetter: React.Dispatch<React.SetStateAction<string | null>>;
  activeQuestionSteps: any[];
  activeQuestionStepsRef: React.MutableRefObject<any[]>;
  activeStepIndex: number;
  activeStepIndexRef: React.MutableRefObject<number>;
  allStepsCompleted: boolean;
  allStepsCompletedRef: React.MutableRefObject<boolean>;
  exerciseQuestions: any[];
  exerciseQuestionsRef: React.MutableRefObject<any[]>;
  exerciseId: number | null;

  // Callbacks from Socratic hook
  selectQuestionFromBubble: (question: any, letter: string) => void;
  loadQuestionSteps: (retry?: boolean, targetQId?: number, targetQ?: any) => void;
  handleShowStepSolution: (index: number, stepsList: any[], qId: number, qLetter: string) => void;
  handleStepTutoring: (index: number, stepsList: any[], qId: number, qLetter: string) => void;
  handleStepSolved: (index: number, stepsList: any[], qId: number, qLetter: string) => void;
  handleExplainMore: (index: number, stepsList: any[], prevResponse: string, qId: number, qLetter: string) => void;
  handleCaptureStart?: () => void;
}

export const useAgentChat = (params: AgentChatParams) => {
  const navigate = useNavigate();
  const {
    isFromLessonList,
    topic_name,
    lessonId,
    levelName,
    lessonData,
    phaseIndex,
    fromDisplayExercise,
    currentStep,
    setCurrentStep,
    setIsTyping,
    messages,
    messagesRef,
    setMessages,
    addBotMessage,
    waitForMessageCompletion,
    studentName,
    user_attributes,
    t,

    selectedQuestionId,
    selectedQuestionIdRef,
    setSelectedQuestionId,
    selectedQuestion,
    setSelectedQuestion,
    selectedQuestionRef,
    selectedQuestionLetter,
    selectedQuestionLetterRef,
    setSelectedQuestionLetter,
    activeQuestionSteps,
    activeQuestionStepsRef,
    activeStepIndex,
    activeStepIndexRef,
    allStepsCompleted,
    allStepsCompletedRef,
    exerciseQuestions,
    exerciseQuestionsRef,
    exerciseId,

    selectQuestionFromBubble,
    loadQuestionSteps,
    handleShowStepSolution,
    handleStepTutoring,
    handleStepSolved,
    handleExplainMore,
    handleCaptureStart
  } = params;

  /**
   * Resumes the structured exercise practice flow from the last active state before an off-topic question.
   */
  const handleResumeFromOffTopic = useCallback(async () => {
    // 1. If no question is selected yet
    if (!selectedQuestionIdRef.current) {
      const genderNum = user_attributes?.gender;
      const genderStr = genderNum === 2 ? 'female' : 'male';
      const promptText = t(`exercisePracticeSelectSectionPrompt_${genderStr}`, { studentName });

      await addBotMessage({
        id: `select-section-prompt-resume-${Date.now()}`,
        text: promptText,
        type: 'text',
        options: [
          {
            label: t("wayToSolution"),
            value: "way_to_solution",
            action: () => {
              const targetQ = exerciseQuestions[0];
              if (targetQ) {
                selectQuestionFromBubble(targetQ, hebrewLetters[0]);
              }
            }
          }
        ]
      });
      return;
    }

    // 2. If a question is selected but steps are not loaded or activeQuestionSteps is empty
    if (activeQuestionStepsRef.current.length === 0) {
      loadQuestionSteps();
      return;
    }

    const qId = selectedQuestionIdRef.current;
    const qLetter = selectedQuestionLetterRef.current || 'א';

    // 3. If all steps are completed
    if (allStepsCompletedRef.current) {
      const history = useQuestionsStore.getState().getQuestionHistory(exerciseId);
      const completed = history?.completed || [];
      const nextQ = exerciseQuestionsRef.current.find((q: any) => !completed.includes(q.id));
      const qLetter = selectedQuestionLetterRef.current || 'א';

      const genderNum = user_attributes?.gender;
      const genderStr = genderNum === 2 ? 'female' : 'male';
      const promptText = nextQ
        ? t(`exercisePracticeQuestionCompleteCelebration_${genderStr}`, { studentName, questionLetter: qLetter })
        : isFromLessonList
          ? t(`exercisePracticeExerciseCompleteInLesson_${genderStr}`, { studentName })
          : t(`exercisePracticeExerciseCompleteCelebration_${genderStr}`, { studentName });

      if (nextQ) {
        const updatedQuestions = exerciseQuestionsRef.current.map((q: any) =>
          completed.includes(q.id) ? { ...q, isCompleted: true } : q
        );
        const listBubbleId = `questions-list-bubble-celebration-resume-${Date.now()}`;
        setMessages(prev => [
          ...prev,
          {
            id: listBubbleId,
            sender: 'bot',
            text: '',
            type: 'questions_list',
            questions: updatedQuestions
          }
        ]);
      }

      const celebrationMsgId = `celebration-resume-${Date.now()}`;
      await addBotMessage({
        id: celebrationMsgId,
        text: promptText,
        type: 'text'
      }, 20);

      await waitForMessageCompletion(celebrationMsgId);
      return;
    }

    // 4. In the middle of steps solving
    const stepIndex = activeStepIndexRef.current;
    const step = activeQuestionStepsRef.current[stepIndex];
    if (!step) return;

    const stepContentMsgId = `step-content-bubble-${step.id}-${stepIndex}`;
    const solutionLoaderIdPrefix = `solver-loader-`;
    const tutorLoaderIdPrefix = `tutor-loader-`;
    const explanationMsgId = `deep-explanation-${step.id}`;

    // Find in messages history
    const stepMessages = messagesRef.current.filter(m => m.id?.includes(step.id) || m.id === stepContentMsgId);
    const lastMsg = stepMessages[stepMessages.length - 1];

    if (lastMsg && lastMsg.id === explanationMsgId) {
      const genderNum = user_attributes?.gender;
      const genderStr = genderNum === 2 ? 'female' : 'male';
      const readyText = t(`exercisePracticeReadyToProceed_${genderStr}`, { studentName });

      await addBotMessage({
        id: `deep-explanation-next-resume-${step.id}`,
        text: readyText,
        type: 'options',
        options: [
          {
            label: t("understoodNextStep"),
            value: "next_step",
            action: () => handleStepSolved(stepIndex, activeQuestionStepsRef.current, qId, qLetter)
          }
        ]
      });
      return;
    }

    const isSolution = lastMsg && (lastMsg.id?.startsWith(solutionLoaderIdPrefix) || lastMsg.id?.startsWith(`solution-followup-`));
    if (isSolution) {
      const genderNum = user_attributes?.gender;
      const genderStr = genderNum === 2 ? 'female' : 'male';
      const furtherExplainText = t(`exercisePracticeFurtherExplanation_${genderStr}`, { studentName });

      await addBotMessage({
        id: `solution-followup-resume-${step.id}`,
        text: furtherExplainText,
        type: 'options',
        options: [
          {
            label: t("exercisePracticeYes"),
            value: "explain_more",
            action: () => handleExplainMore(stepIndex, activeQuestionStepsRef.current, lastMsg.text, qId, qLetter)
          },
          {
            label: t("understoodNextStep"),
            value: "next_step",
            action: () => handleStepSolved(stepIndex, activeQuestionStepsRef.current, qId, qLetter)
          }
        ]
      });
      return;
    }



    const genderNum = user_attributes?.gender;
    const genderStr = genderNum === 2 ? 'female' : 'male';
    const notebookPromptText = t(`stepSolveInNotebook_${genderStr}`);

    await addBotMessage({
      id: `step-notebook-prompt-resume-${step.id}-${stepIndex}`,
      text: notebookPromptText,
      type: 'options',
      optionsClassName: 'step-notebook-prompt-actions',
      options: getNotebookPromptOptions(
        t,
        () => handleShowStepSolution(stepIndex, activeQuestionStepsRef.current, qId, qLetter),
        () => handleStepTutoring(stepIndex, activeQuestionStepsRef.current, qId, qLetter),
        showInfoToast,
        handleCaptureStart
      )
    });
  }, [
    selectedQuestionIdRef,
    selectedQuestionLetterRef,
    activeQuestionStepsRef,
    activeStepIndexRef,
    allStepsCompletedRef,
    exerciseQuestionsRef,
    exerciseId,
    studentName,
    user_attributes,
    addBotMessage,
    loadQuestionSteps,
    selectQuestionFromBubble,
    handleShowStepSolution,
    handleStepTutoring,
    handleStepSolved,
    handleExplainMore,
    messagesRef,
    t
  ]);

  /**
   * Submits a chat query message to the AI Assistant endpoint
   */
  const sendMessageToAgent = useCallback(async (text: string, currentMessages: Message[]) => {
    setIsTyping(true);
    try {
      const payload = {
        message: text,
        context: {
          current_page: "exercise-practice",
          topic: topic_name,
          step_type: lessonData?.phase || "unknown",
          lesson_id: lessonId,
          metadata: {
            sub_topic: "",
            lesson_phase: lessonData?.phase,
            phase_index: phaseIndex,
            exercise_id: exerciseId,
            selected_question_id: selectedQuestionIdRef.current,
            selected_step_id: activeQuestionStepsRef.current?.[activeStepIndexRef.current]?.id,
            gender: user_attributes?.gender === 2 ? 'female' : 'male'
          }
        },
        history: currentMessages.map(m => ({ role: m.sender === 'bot' ? 'assistant' : 'user', content: m.text }))
      };
      const response = await chatService.post('/agent/chat', payload);
      if (response) {
        if (response.client_action && response.client_action.type === 'confirm_navigate_learning') {
          const genderNum = user_attributes?.gender;
          const genderStr = genderNum === 2 ? 'female' : 'male';
          const messageText = t(`exercisePracticeConfirmNavigateLearning_${genderStr}`, { studentName });

          await addBotMessage({
            id: `confirm-navigate-learning-${Date.now()}`,
            text: messageText,
            type: 'options',
            options: [
              {
                label: t("exercisePracticeConfirmNavigateLearningYes"),
                value: "yes",
                action: () => navigate(ROUTERS_PATH.SEARCH_LESSON)
              },
              {
                label: t("exercisePracticeConfirmNavigateLearningNo"),
                value: "continue_practice",
                action: () => handleResumeFromOffTopic()
              }
            ]
          });
          return;
        }

        if (response.message) {
          if (response.intent === 'off_topic') {
            const genderNum = user_attributes?.gender;
            const genderStr = genderNum === 2 ? 'female' : 'male';

            if (!selectedQuestionIdRef.current) {
              const offTopicMsg = t(`exercisePracticeLessonTutoringOffTopic_${genderStr}`, { studentName });
              await addBotMessage({
                id: `off-topic-response-${Date.now()}`,
                text: offTopicMsg,
                type: 'options',
                options: [
                  {
                    label: t("letsStartPracticing"),
                    value: "start_practice",
                    action: () => setCurrentStep(PracticeStep.RECOMMEND)
                  }
                ]
              });
            } else {
              const offTopicMsg = t(`exercisePracticeOffTopic_${genderStr}`, { studentName });
              await addBotMessage({
                id: `off-topic-response-${Date.now()}`,
                text: offTopicMsg,
                type: 'options',
                options: [
                  {
                    label: t("exercisePracticeContinueFemale"), // "המשיכי"
                    value: "continue",
                    action: () => handleResumeFromOffTopic()
                  }
                ]
              });
            }
          } else if (response.intent === 'lesson_tutoring' || !selectedQuestionIdRef.current) {
            await addBotMessage({
              id: `agent-tutoring-response-${Date.now()}`,
              text: response.message,
              type: 'options',
              options: [
                {
                  label: t("letsStartPracticing"),
                  value: "start_practice",
                  action: () => setCurrentStep(PracticeStep.RECOMMEND)
                }
              ]
            });
          } else {
            // Math-related agent response
            const resumeParams = {
              selectedQuestionId: selectedQuestionIdRef.current,
              selectedQuestionLetter: selectedQuestionLetterRef.current,
              activeQuestionSteps: activeQuestionStepsRef.current,
              activeStepIndex: activeStepIndexRef.current,
              allStepsCompleted: allStepsCompletedRef.current,
              exerciseQuestions: exerciseQuestionsRef.current,
              exerciseId,
              messages: currentMessages,
              t,
              selectQuestionFromBubble,
              loadQuestionSteps,
              handleShowStepSolution,
              handleStepTutoring,
              handleStepSolved,
              showInfoToast,
              handleCaptureStart
            };
            const resumeOptions = getCurrentStepOptions(resumeParams);
             const isNotebookPrompt = resumeOptions.some(opt => opt.value === 'capture');
             await addBotMessage({
               id: `agent-chat-response-${Date.now()}`,
               text: response.message,
               type: resumeOptions.length > 0 ? 'options' : 'text',
               options: resumeOptions,
               optionsClassName: isNotebookPrompt ? 'solution-capture-options-layout' : undefined
             });
          }
      } else {
        await addBotMessage({ text: t('generalError') });
      }
    } else {
      await addBotMessage({ text: t('generalError') });
    }
    } catch (e) {
      await addBotMessage({ text: t('generalError') });
    } finally {
      setIsTyping(false);
    }
  }, [
    topic_name,
    lessonData,
    lessonId,
    phaseIndex,
    user_attributes,
    studentName,
    addBotMessage,
    handleResumeFromOffTopic,
    selectedQuestionIdRef,
    selectedQuestionLetterRef,
    activeQuestionStepsRef,
    activeStepIndexRef,
    allStepsCompletedRef,
    exerciseQuestionsRef,
    exerciseId,
    selectQuestionFromBubble,
    loadQuestionSteps,
    handleShowStepSolution,
    handleStepTutoring,
    handleStepSolved,
    t,
    setIsTyping,
    handleCaptureStart
  ]);

  /**
   * Action when the student sends a chat input message
   */
  const addUserMessage = useCallback((text: string) => {
    const newMessage: Message = {
      id: Date.now().toString() + Math.random(),
      sender: 'user',
      text,
      type: 'text'
    };

    setMessages(prev => {
      const newMessages = [...prev, newMessage];
      if (currentStep === PracticeStep.AI_CHAT || fromDisplayExercise) {
        sendMessageToAgent(text, newMessages);
      } else if (currentStep === PracticeStep.GREETING) {
        setCurrentStep(PracticeStep.AI_CHAT);
        sendMessageToAgent(text, newMessages);
      } else {
        addBotMessage({
          text: t('exercisePracticeEncourage'),
          type: 'options',
          options: [
            {
              label: t('exercisePracticeFeelsProgress'),
              value: 'progress',
              action: () => setCurrentStep(PracticeStep.RECOMMEND)
            }
          ]
        });
      }
      return newMessages;
    });
  }, [addBotMessage, currentStep, sendMessageToAgent, t, fromDisplayExercise, setMessages, setCurrentStep]);

  /**
   * Action when student choices their progression status (greeting prompt feedback options)
   */
  const handleProgressChoice = useCallback((choice: 'progress' | 'not_yet') => {
    if (choice === 'progress') {
      setCurrentStep(PracticeStep.RECOMMEND);
    } else {
      setCurrentStep(PracticeStep.AI_CHAT);
      sendMessageToAgent("[INTENT: NOT_UNDERSTOOD]", messagesRef.current);
    }
  }, [sendMessageToAgent, setCurrentStep, messagesRef]);

  return {
    sendMessageToAgent,
    handleResumeFromOffTopic,
    addUserMessage,
    handleProgressChoice
  };
};
